import { useState } from "react";
import { Link } from "react-router-dom";
import {
  ChevronDown, ChevronRight, X, UserPlus, ListChecks,
} from "lucide-react";
import { ContactPicker } from "@/components/ContactPicker";
import { useOpenContactPanel } from "@/components/sidepanel/registry";
import type {
  AccordionPayload, GewerkStatus, GewerkContactLink, Contact,
} from "@/types";

const gewerkStatusLabels: Record<GewerkStatus, string> = {
  zu_erledigen: 'Zu erledigen',
  in_arbeit: 'In Arbeit',
  fertig: 'Fertig',
};

const gewerkStatusClasses: Record<GewerkStatus, string> = {
  zu_erledigen: 'bg-gray-100 text-gray-800',
  in_arbeit: 'bg-amber-100 text-amber-800',
  fertig: 'bg-green-100 text-green-800',
};

const gewerkStatusOrder: GewerkStatus[] = ['zu_erledigen', 'in_arbeit', 'fertig'];

function contactName(contact: GewerkContactLink): string {
  return (
    contact.display_name ||
    [contact.first_name, contact.last_name].filter(Boolean).join(' ') ||
    contact.company_name ||
    contact.email_address ||
    'Unbenannter Kontakt'
  );
}

interface GewerkAccordionProps {
  payload: AccordionPayload;
  isApprover: boolean;
  onStatusChange: (gewerkId: number, status: GewerkStatus) => Promise<void>;
  onAddContact: (gewerkId: number, contact: Contact) => Promise<void>;
  onRemoveContact: (gewerkId: number, linkId: number) => Promise<void>;
}

/**
 * Bauablauf-Checkliste: der gesamte aktive Gewerk-Katalog, gruppiert
 * nach Phase als Akkordeon. Genehmiger/Admin setzen Status und Kontakte,
 * alle anderen sehen Status-Badge und Kontakt-Chips lesend.
 */
export function GewerkAccordion({
  payload,
  isApprover,
  onStatusChange,
  onAddContact,
  onRemoveContact,
}: GewerkAccordionProps) {
  const openContactInPanel = useOpenContactPanel();
  // Kontakt-Chips öffnen die Split-View-Seitenleiste statt zu navigieren;
  // Modifikator-Klick / mittlere Maustaste bleibt natives Browser-Verhalten.
  const openContactFromLink = (e: React.MouseEvent, contactId: number, name?: string) => {
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
    e.preventDefault();
    openContactInPanel(contactId, name);
  };
  // Erste Phase expandiert, Rest kollabiert (Lese-Reihenfolge des Ablaufs).
  const [expandedPhaseIds, setExpandedPhaseIds] = useState<Set<number>>(
    () => new Set(payload.phases.length > 0 ? [payload.phases[0].id] : [])
  );
  // EIN geteilter Picker — rendert unter der Zeile, für die er geöffnet wurde.
  const [pickerGewerkId, setPickerGewerkId] = useState<number | null>(null);

  const togglePhase = (phaseId: number) => {
    setExpandedPhaseIds((prev) => {
      const next = new Set(prev);
      if (next.has(phaseId)) {
        next.delete(phaseId);
      } else {
        next.add(phaseId);
      }
      return next;
    });
  };

  return (
    <div className="space-y-3">
      {payload.phases.map((phase) => {
        const expanded = expandedPhaseIds.has(phase.id);
        const doneCount = phase.gewerke.filter((g) => g.status === 'fertig').length;
        return (
          <div key={phase.id} className="rounded-lg border border-gray-200 bg-white">
            <button
              type="button"
              onClick={() => togglePhase(phase.id)}
              className="flex w-full items-center gap-2 px-4 py-3 text-left hover:bg-gray-50"
              aria-expanded={expanded}
            >
              {expanded
                ? <ChevronDown className="h-4 w-4 shrink-0 text-gray-400" />
                : <ChevronRight className="h-4 w-4 shrink-0 text-gray-400" />}
              <span className="flex-1 text-sm font-semibold text-gray-900">{phase.name}</span>
              <span className="text-xs text-gray-500">
                {doneCount} fertig / {phase.gewerke.length} Gewerke
              </span>
            </button>
            {expanded && (
              phase.gewerke.length === 0 ? (
                <p className="px-4 pb-3 text-sm text-gray-500">Keine aktiven Gewerke in dieser Phase.</p>
              ) : (
                <ul className="divide-y divide-gray-100 border-t border-gray-100">
                  {phase.gewerke.map((gewerk) => (
                    <li key={gewerk.id} className="px-4 py-3">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <span className="text-sm text-gray-900">{gewerk.name}</span>
                        {isApprover ? (
                          <div className="flex gap-1">
                            {gewerkStatusOrder.map((status) => (
                              <button
                                key={status}
                                type="button"
                                onClick={() => onStatusChange(gewerk.id, status)}
                                className={`rounded-full px-2.5 py-0.5 text-xs font-medium transition-colors ${
                                  gewerk.status === status
                                    ? gewerkStatusClasses[status]
                                    : 'border border-gray-200 bg-white text-gray-500 hover:border-gray-300 hover:text-gray-700'
                                }`}
                              >
                                {gewerkStatusLabels[status]}
                              </button>
                            ))}
                          </div>
                        ) : (
                          <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${gewerkStatusClasses[gewerk.status]}`}>
                            {gewerkStatusLabels[gewerk.status]}
                          </span>
                        )}
                      </div>
                      {(gewerk.contacts.length > 0 || isApprover) && (
                        <div className="mt-2 flex flex-wrap items-center gap-2">
                          {gewerk.contacts.map((contact) => (
                            <span
                              key={contact.link_id}
                              className="inline-flex items-center gap-1 rounded-full bg-gray-100 pl-2.5 pr-1 py-0.5 text-xs text-gray-800"
                            >
                              <Link
                                to={`/contacts/${contact.contact_id}`}
                                onClick={(e) => openContactFromLink(e, contact.contact_id, contactName(contact))}
                                className="hover:text-primary-700"
                              >
                                {contactName(contact)}
                              </Link>
                              {isApprover && (
                                <button
                                  type="button"
                                  onClick={() => onRemoveContact(gewerk.id, contact.link_id)}
                                  className="rounded-full p-0.5 text-gray-400 hover:bg-red-50 hover:text-red-600"
                                  title="Zuordnung entfernen"
                                >
                                  <X className="h-3 w-3" />
                                </button>
                              )}
                            </span>
                          ))}
                          {isApprover && (
                            <button
                              type="button"
                              onClick={() => setPickerGewerkId(pickerGewerkId === gewerk.id ? null : gewerk.id)}
                              className="inline-flex items-center gap-1 rounded-full border border-dashed border-gray-300 px-2.5 py-0.5 text-xs text-gray-500 hover:border-primary-300 hover:text-primary-700"
                              title="Kontakt zuordnen"
                            >
                              <UserPlus className="h-3 w-3" />
                              Kontakt
                            </button>
                          )}
                        </div>
                      )}
                      {pickerGewerkId === gewerk.id && (
                        <div className="mt-2">
                          <ContactPicker
                            placeholder="Kontakt für dieses Gewerk suchen…"
                            onSelect={(contact) => {
                              const pickedGewerkId = gewerk.id;
                              setPickerGewerkId(null);
                              onAddContact(pickedGewerkId, contact);
                            }}
                          />
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              )
            )}
          </div>
        );
      })}
      {payload.phases.length === 0 && (
        <p className="text-sm text-gray-500">
          <ListChecks className="mr-1 inline h-4 w-4 text-gray-400" />
          Der Gewerk-Katalog ist noch leer.
        </p>
      )}
    </div>
  );
}